import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import DOMPurify from "dompurify";
import { ArrowLeft, Clock } from "lucide-react";

// Configure DOMPurify once (allow safe formatting tags from Quill editor)
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "ol", "ul", "li", "blockquote", "a", "h2", "h3", "h4", "code", "pre"],
  ALLOWED_ATTR: ["href", "target", "rel"],
  ALLOW_DATA_ATTR: false,
};

function sanitizePostContent(raw) {
  if (!raw) return "";
  // If the content is plain paragraphs separated by blank lines, wrap them.
  const hasHtmlTags = /<\/?(p|br|h[1-6]|ul|ol|li|blockquote|strong|em)/i.test(raw);
  const html = hasHtmlTags ? raw : `<p>${raw.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br/>")}</p>`;
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

export default function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true); setError(false);
    http.get(`/posts/${slug}`)
      .then((r) => { setData(r.data); document.title = `${r.data.post.title} — Blog`; })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container-editorial py-24" data-testid="post-loading">Caricamento…</div>;
  if (error || !data) return (
    <div className="container-editorial py-24" data-testid="post-not-found">
      <p>Articolo non trovato.</p>
      <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-fucsia"><ArrowLeft size={16} /> Torna al blog</Link>
    </div>
  );

  const { post, related } = data;

  return (
    <article data-testid={`page-post-${post.slug}`}>
      <div className="container-editorial py-12 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-viola/70 hover:text-viola"><ArrowLeft size={14} /> Blog</Link>
        <div className="mt-6 flex flex-wrap gap-2">
          {(post.categories || []).map((c) => <span key={c} className="pill">{c}</span>)}
        </div>
        <h1 className="mt-4 font-display text-4xl sm:text-6xl tracking-tighter leading-[1.02]">{post.title}</h1>
        <p className="mt-6 flex items-center gap-4 text-sm text-viola/70">
          <span>di {post.author}</span>
          <span>·</span>
          <span>{new Date(post.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {post.read_minutes || 5} min</span>
        </p>
      </div>

      {post.cover_url && (
        <div className="container-editorial max-w-5xl">
          <div className="aspect-[16/9] overflow-hidden rounded-2xl">
            <img src={post.cover_url} alt={post.title} className="h-full w-full object-cover" />
          </div>
        </div>
      )}

      <div className="container-editorial max-w-3xl py-16">
        <div
          className="prose prose-lg max-w-none text-viola/85 leading-relaxed [&>p]:mb-6 [&>h2]:font-display [&>h2]:text-3xl [&>h2]:mt-10 [&>h2]:mb-4 [&>h3]:font-display [&>h3]:text-2xl [&>h3]:mt-8 [&>h3]:mb-3 [&>a]:text-fucsia"
          dangerouslySetInnerHTML={{ __html: sanitizePostContent(post.content) }}
        />
        {post.tags?.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2 pt-8 border-t border-viola/10">
            {post.tags.map((t) => <span key={t} className="pill">#{t}</span>)}
          </div>
        )}
      </div>

      {related?.length > 0 && (
        <section className="border-t border-viola/10 bg-lilla/30">
          <div className="container-editorial py-16">
            <h2 className="font-display text-3xl">Articoli correlati</h2>
            <div className="mt-8 grid md:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link to={`/blog/${p.slug}`} key={p.id} className="group" data-testid={`related-${p.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden rounded-xl bg-cream mb-4">
                    {p.cover_url ? <img src={p.cover_url} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="h-full w-full bg-gradient-to-br from-lilla to-arancio/40" />}
                  </div>
                  <h3 className="font-display text-xl leading-tight group-hover:text-fucsia transition-colors">{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
